import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WebStoriesIcon from "@mui/icons-material/WebStories";
import GroupIcon from "@mui/icons-material/Group";
import useStyles from "../dashAuction.style";
import eventDetailsAPI from "../../../../api/adminAPI/eventDetailsAPI";
import Typography from "@mui/material/Typography";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getTimeZone, localTimeZone } from "../../../../common";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import "react-data-table-component-extensions/dist/index.css";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { toast } from "react-toastify";
import { FaRegFileExcel, FaRegFilePdf } from "react-icons/fa";

import deleteBidAPI from "../../../../api/deleteBidAPI";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import getEventReportData from "../../../../api/getEventReportData";
import {
  exportAuctionDetailsToXLSX,
  exportLotsListingToXLSX,
} from "./auctionExcelExport";
import { exportLotsListingToPDF } from "./auctionPdfExport";
import { exportAuctionReportToXLSX } from "./auctionFullReportExport";
import {
  formatDateTime,
  hasLeadBid,
  offerNote,
} from "./auctionReportData";
import { dateSortValue, now } from "../../../../common/dateTime";

const AuctionView = ({
  Bidlog,
  BidlogSecond,
  eventId,
  AuctionTable,
  changeView,
}) => {
  const classes = useStyles();
  const [tableEventItemsData, setTableEventItemsData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [deletingBid, setDeletingBid] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBidDetails, setDeleteBidDetails] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [fetchingReportLoader, setFetchingReportLoader] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    eventsDetails(eventId);
    return () => {
      return false;
    };
  }, [eventId]);

  const getZone = getTimeZone() ? getTimeZone() : localTimeZone();

  const handleClick = (event, eventId) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // setEventId("");
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const eventsDetails = async (eventId) => {
    try {
      const { data } = await eventDetailsAPI(eventId);

      if (data) {
        let id = 1;

        const currentDate = now();
        const auctionEndDate = moment(data.bidEndDateTime).add(48, "hours"); // auction end date + 48 hours
        const auctionEnded = auctionEndDate.isBefore(currentDate);
        const eventsData = data.eventItems.map((eventItem, index) => {
          return {
            id: id++,
            lotNumber: eventItem.lotNumber,
            shortDescription: eventItem.shortDescription,
            quantity: eventItem.quantity,
            unit: eventItem.unit,
            startPrice: eventItem.startPrice,
            increment: eventItem.increment,
            participants: eventItem.participants,
            totalBidders: eventItem.totalBidders,
            totalBids: eventItem.totalBids,
            highestBid: eventItem.highestBid,
            highestBidder: hasLeadBid(eventItem)
              ? eventItem.highestBidder
              : "No bid",
            highestBidId: eventItem.highestBidId,
            leadStage: eventItem.leadStage,
            leadBelowStartPrice: eventItem.leadBelowStartPrice,
            leadValue: eventItem.leadValue,
            bestPrebid: eventItem.bestPrebid,
            bestPostBid: eventItem.bestPostBid,
            buyers: eventItem.buyers,
            startAt: eventItem.startDateAndTime,
            endAt: eventItem.endDateAndTime,
            startDateAndTime: formatDateTime(
              eventItem.startDateAndTime,
              getZone
            ),
            endDateAndTime: formatDateTime(
              eventItem.endDateAndTime,
              getZone,
              data.eventFormat
            ),
            action: "hello",
            eventItemId: eventItem.eventItemId,
            auctionEnded,
          };
        });

        setTableEventItemsData(eventsData);
        setEventData(data);
      } else throw new Error(data);
    } catch (error) {
      console.log(error);
    }
  };

  const deleteBidHandler = async (data) => {
    const details = {
      eventItemId: data.eventItemId,
      bidId: data.highestBidId,
    };
    setDeleteBidDetails(details);
    setDeleteDialogOpen(true);
  };

  const downloadReport = async () => {
    setFetchingReportLoader(true);
    try {
      const { data } = await getEventReportData(eventId);
      exportAuctionReportToXLSX({ report: data, zone: getZone });
    } catch (error) {
      console.log(error);
      toast.error("Report not created. Something went wrong!");
    }
    setFetchingReportLoader(false);
  };

  const columns = [
    {
      name: "Auction ID",
      cell: (data) => String(data.auctionId),
      sortable: true,
      width: "150px",
    },
    {
      name: "Description",
      cell: (data) => {
        return (
          <Tooltip
            title={data.shortDesc}
            placement="top-start"
            TransitionComponent={Zoom}
          >
            <span className={classes.ellipsisText}>{data.shortDesc}</span>
          </Tooltip>
        );
      },
      sortable: false,
      width: "180px",
    },
    {
      name: "Start Date & Time",
      cell: (data) => String(data.startDT),
      sortable: false,
      width: "180px",
    },
    {
      name: "End Date & Time",
      cell: (data) => String(data.endDT),
      sortable: false,
      width: "180px",
    },
    {
      name: "No. of Lots",
      cell: (data) => String(data.numLot),
      sortable: false,
    },
    {
      name: "Invited bidders",
      cell: (data) => String(data.participant),
      sortable: false,
      width: "120px",
    },
    {
      name: "Bidders bid",
      cell: (data) => String(data.bidder),
      sortable: false,
    },
    {
      name: "Invited, no bid",
      width: "130px",
      cell: (data) => String(data.unBidder),
      sortable: false,
    },
    {
      name: "Action",
      cell: (data) => (
        <div>
          <Button
            aria-describedby={data.eventId}
            variant="contained"
            onClick={(event) => {
              handleClick(event, data.eventId);
            }}
          >
            <MoreVertIcon />
          </Button>
          <Popover
            id={data.eventId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Typography sx={{ p: 1, display: "grid", px: 2 }}>
              <div
                className={classes.viewBtn}
                onClick={() => {
                  Bidlog(data.eventId);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <GroupIcon
                  style={{
                    color: "#2D55EB",
                    paddingRight: "5px",
                    marginRight: ".4rem",
                  }}
                />
                Buyers
              </div>
              <div
                className={classes.viewBtn}
                onClick={() => changeView("AuctionDetailLogs")}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <WebStoriesIcon
                  style={{
                    color: "#2D55EB",
                    paddingRight: "5px",
                    marginRight: ".4rem",
                  }}
                />
                Logs
              </div>
              <div
                className={classes.viewBtn}
                onClick={() => downloadReport()}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {fetchingReportLoader ? (
                  <CircularProgress
                    size={"1.1rem"}
                    style={{
                      marginRight: ".6rem",
                    }}
                  />
                ) : (
                  <FaRegFileExcel
                    style={{
                      fontSize: "25px",
                      color: "#04aa18",
                      paddingTop: "4px",
                      marginRight: ".4rem",
                      marginLeft: "-.2rem",
                    }}
                  />
                )}
                Download Report
              </div>
            </Typography>
          </Popover>
        </div>
      ),
    },
  ];

  const nextColumns = [
    {
      name: "Sr.",
      cell: (data) => String(data.id),
      sortable: true,
      width: "80px",
    },
    {
      name: "Lot No",
      selector: (data) => data.lotNumber,
      // natural order: "2" before "10", "LOT-9" before "LOT-10"
      sortFunction: (a, b) =>
        String(a.lotNumber).localeCompare(String(b.lotNumber), undefined, {
          numeric: true,
        }),
      cell: (data) => String(data.lotNumber),
      sortable: true,
      width: "120px",
    },
    {
      name: "Short Description",
      cell: (data) => {
        return (
          <Tooltip
            title={data.shortDescription}
            placement="top-start"
            TransitionComponent={Zoom}
          >
            <span className={classes.ellipsisText}>
              {data.shortDescription}
            </span>
          </Tooltip>
        );
      },
      sortable: true,
      width: "180px",
    },

    {
      name: "Qty",
      cell: (data) => String(data.quantity),
      sortable: true,
    },
    {
      name: "Unit",
      cell: (data) => String(data.unit),
      sortable: true,
    },
    {
      name: `Start Price ${eventData.currency}`,
      cell: (data) => String(data.startPrice),
      sortable: true,
      width: "150px",
    },
    {
      name: `${
        eventData.eventType === "FORWARD_AUCTION" ? "Increment" : "Decrement"
      } ${
        eventData.incrementBidAmountBy === "PERCENTAGE"
          ? "%"
          : eventData.currency
      }`,
      cell: (data) => String(data.increment),
      sortable: true,
      width: "150px",
    },
    {
      name: "Start Date & Time",
      selector: (data) => dateSortValue(data.startAt),
      cell: (data) => String(data.startDateAndTime),
      sortable: true,
      width: "200px",
    },
    {
      name: "End Date & Time",
      selector: (data) => dateSortValue(data.endAt),
      cell: (data) => String(data.endDateAndTime),
      sortable: true,
      width: "200px",
    },
    {
      name: "Invited bidders",
      cell: (data) => String(data.participants),
      sortable: true,
      width: "120px",
    },
    {
      name: "Bidders bid",
      cell: (data) => String(data.totalBidders),
      sortable: true,
    },
    {
      name: "No. of Bids",
      cell: (data) => String(data.totalBids),
      sortable: true,
      width: "120px",
    },
    {
      name: "Lead Bidder",
      cell: (data) => String(data.highestBidder),
      sortable: true,
      width: "120px",
    },
    {
      name: `Lead Bid (${eventData.currency})`,
      cell: (data) => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "2rem",
            }}
          >
            <div style={{ textAlign: "right" }}>
              {String(data.highestBid || "-")}
              {hasLeadBid(data) && (
                <div style={{ fontSize: "11px", color: "#666" }}>
                  {offerNote(
                    {
                      stage: data.leadStage,
                      belowStartPrice: data.leadBelowStartPrice,
                    },
                    eventData.eventType
                  )}
                </div>
              )}
            </div>
            {!data.auctionEnded && (
              <span
                style={{
                  marginLeft: "1rem",
                }}
              >
                {/* only prebids and auction bids can be deleted here; a
                    post-bid lead has no bid id */}
                <Tooltip
                  title={
                    data.highestBid && !data.highestBidId
                      ? "Post-bid delete coming soon"
                      : "delete bid"
                  }
                >
                  <DeleteOutlinedIcon
                    onClick={() => data.highestBidId && deleteBidHandler(data)}
                    style={{
                      fontSize: "1.4rem",
                      color: data.highestBidId ? "red" : "grey",
                      cursor: data.highestBidId ? "pointer" : "not-allowed",
                    }}
                  />
                </Tooltip>
              </span>
            )}
          </div>
        );
      },
      sortable: true,
      width: "150px",
    },
    {
      name: `Lead Value (${eventData.currency})`,
      cell: (data) => String(data.leadValue || "-"),
      sortable: true,
      width: "150px",
    },
    // shown only when some lot of the auction has a prebid / post-bid
    ...(eventData.hasPrebids
      ? [
          {
            name: `Best Prebid (${eventData.currency})`,
            cell: (data) => String(data.bestPrebid ?? "-"),
            sortable: true,
            width: "150px",
          },
        ]
      : []),
    ...(eventData.hasPostBids
      ? [
          {
            name: `Best Post-bid (${eventData.currency})`,
            cell: (data) => String(data.bestPostBid ?? "-"),
            sortable: true,
            width: "150px",
          },
        ]
      : []),
    {
      name: "Buyer's Bid",
      sortable: true,
      width: "150px",
      cell: (data) => (
        <div>
          {data.buyers.map((buyer, index) => {
            return (
              <Typography sx={{ fontSize: "13px" }}>
                <div>
                  <span className={classes.viewBtn}>
                    {buyer["users.firstName"]} {buyer["users.lastName"]} -{" "}
                    {buyer["amount"]} -{" "}
                    {eventData.eventType === "REVERSE_AUCTION" ? "L" : "H"}
                    {buyer.rank ?? index + 2}{" "}
                    {offerNote(buyer, eventData.eventType)}
                  </span>
                </div>
              </Typography>
            );
          })}
        </div>
      ),
    },
    {
      name: "Action",
      sortable: true,
      cell: (data) => (
        <div>
          <Button
            className="btn btn-group"
            onClick={() => {
              BidlogSecond(data.eventItemId);
            }}
          >
            Bidlog
          </Button>
        </div>
      ),
    },
  ];

  const data = [
    {
      auctionId: eventData.eventNumber,
      shortDesc: eventData.description,
      startDT: formatDateTime(eventData.bidStartDateTime, getZone),
      endDT: formatDateTime(
        eventData.bidEndDateTime,
        getZone,
        eventData.eventFormat
      ),
      numLot: eventData.totalLots,
      participant: eventData.participants,
      bidder: eventData.totalBuyers,
      unBidder: eventData.totalUnBidedLots,
      action: "hello",
    },
  ];

  const handleDeleteConfirmDialogClose = () => {
    setDeleteBidDetails(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteBidConfirm = async () => {
    if (deleteBidDetails) {
      try {
        const { bidId, eventItemId } = deleteBidDetails;
        setDeletingBid(bidId);
        const deleteBidResponse = await deleteBidAPI({
          bidId,
          eventId,
          eventItemId,
        });

        // if bid delete response is positive
        if (deleteBidResponse.status === 200) {
          toast.info("Bid deleted!");
          eventsDetails(eventId);
        } else {
          toast.error("Bid not deleted. Something went wrong!");
        }
      } catch (error) {
        toast.error("Bid not deleted. Something went wrong!");
      }
      setDeletingBid(null);
    }
    handleDeleteConfirmDialogClose();
  };

  const AuctionDetailsExportButton = () => (
    <Button
      onClick={() =>
        exportAuctionDetailsToXLSX({
          eventData,
          lots: tableEventItemsData,
          zone: getZone,
        })
      }
    >
      <FaRegFileExcel
        style={{ fontSize: "30px", color: "#04aa18", paddingTop: "4px" }}
      />
    </Button>
  );

  const AuctionLotsListingExportButton = () => (
    <Button
      onClick={() =>
        exportLotsListingToXLSX({
          eventData,
          lots: tableEventItemsData,
          zone: getZone,
        })
      }
    >
      <FaRegFileExcel
        style={{ fontSize: "30px", color: "#04aa18", paddingTop: "4px" }}
      />
    </Button>
  );

  const downloadLotsListingPdf = async () => {
    setExportingPdf(true);
    try {
      await exportLotsListingToPDF({
        eventData,
        lots: tableEventItemsData,
        zone: getZone,
      });
    } catch (error) {
      console.log(error);
      toast.error("PDF not created. Something went wrong!");
    }
    setExportingPdf(false);
  };

  const AuctionLotsListingPdfButton = () => (
    <Tooltip title="Download PDF">
      <span>
        <Button onClick={downloadLotsListingPdf} disabled={exportingPdf}>
          {exportingPdf ? (
            <CircularProgress size={"1.6rem"} />
          ) : (
            <FaRegFilePdf
              style={{ fontSize: "28px", color: "#d32f2f", paddingTop: "4px" }}
            />
          )}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteConfirmDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Bid?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this bid ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteBidConfirm} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <div className="AuctionTable " source="ADMIN||SELLER">
        <div className="AuctionView">
          <h3>
            <Button sx={{ color: "#000" }} onClick={AuctionTable}>
              <ArrowBackIcon />
            </Button>
            Auctions Name - {eventData.auctionTitle}
          </h3>
          <DataTableExtensions
            print={false}
            export={false}
            exportHeaders={false}
            columns={columns}
            data={data}
          >
            <DataTable
              noHeader={false}
              defaultSortField="id"
              defaultSortAsc={false}
              pagination={false}
              highlightOnHover
              className="DataTable"
              columns={columns}
              data={data}
              actions={<AuctionDetailsExportButton />}
            />
          </DataTableExtensions>
        </div>
      </div>
      <div className="AuctionTable LotsTableFrozen mt-5">
        <h3>Lots Listing</h3>
        <DataTableExtensions
          print={false}
          export={false}
          exportHeaders={false}
          columns={nextColumns}
          data={tableEventItemsData}
        >
          <DataTable
            noHeader={false}
            pagination={false}
            highlightOnHover
            className="DataTable"
            actions={
              <>
                <AuctionLotsListingExportButton />
                <AuctionLotsListingPdfButton />
              </>
            }
          />
        </DataTableExtensions>
      </div>
    </>
  );
};

export default AuctionView;
